import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  User,
  Mail,
  Lock,
  Phone,
  Eye,
  EyeOff,
  UserPlus,
  Loader2,
  Check,
  ShieldCheck,
  Sun,
  Sparkles,
  Zap,
  Shield,
  Globe,
  CheckCircle,
} from "lucide-react";
import { toast } from "react-toastify";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { API_BASE_URL } from "../../config";

const Register = () => {
  const [formData, setFormData] = useState({
    name: "",
    username: "",
    email: "",
    password: "",
    phone: "",
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isHovered, setIsHovered] = useState({});
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) {
      toast.error("Please agree to the terms of service");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_BASE_URL}/api/auth/register`, formData);
      toast.success(
        "Welcome to the smartitbox revolution! Account created successfully!",
      );
      window.location.reload();
    } catch (error) {
      toast.error(
        "Registration failed. " +
          (error.response?.data?.message || "Unexpected error occurred."),
      );
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength =
    formData.password.length >= 8
      ? formData.password.length >= 12
        ? "strong"
        : "medium"
      : "weak";

  const handleMouseEnter = (field) => {
    setIsHovered((prev) => ({ ...prev, [field]: true }));
  };

  const handleMouseLeave = (field) => {
    setIsHovered((prev) => ({ ...prev, [field]: false }));
  };

  return (
    <div className="space-y-1">
      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Two Column Grid for Name & Username */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <User className="w-4 h-4 text-amber-500" />
              Full Name
            </label>
            <div
              className="relative group"
              onMouseEnter={() => handleMouseEnter("name")}
              onMouseLeave={() => handleMouseLeave("name")}
            >
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-gray-500 group-hover:text-amber-500 transition-colors" />
              </div>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Doe"
                className="w-full pl-12 pr-4 py-3 border border-amber-200/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400/30 focus:border-amber-300 transition-all duration-300 bg-white/50   text-gray-900 placeholder-gray-500 hover:bg-white/80 hover:border-amber-300"
                required
              />
              {/* Sun rays effect */}
              {isHovered.name && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.1 }}
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-100 to-transparent blur-sm pointer-events-none"
                />
              )}
            </div>
          </div>

          {/* Username */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <User className="w-4 h-4 text-yellow-500" />
              Username
            </label>
            <div
              className="relative group"
              onMouseEnter={() => handleMouseEnter("username")}
              onMouseLeave={() => handleMouseLeave("username")}
            >
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-gray-500 group-hover:text-yellow-500 transition-colors" />
              </div>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder="johndoe"
                className="w-full pl-12 pr-4 py-3 border border-yellow-200/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400/30 focus:border-yellow-300 transition-all duration-300 bg-white/50 text-gray-900 placeholder-gray-500 hover:bg-white/80 hover:border-yellow-300"
                required
              />
            </div>
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <Mail className="w-4 h-4 text-orange-500" />
            Email Address
          </label>
          <div
            className="relative group"
            onMouseEnter={() => handleMouseEnter("email")}
            onMouseLeave={() => handleMouseLeave("email")}
          >
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-gray-500 group-hover:text-orange-500 transition-colors" />
            </div>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="john@example.com"
              className="w-full pl-12 pr-4 py-3 border border-orange-200/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-300 transition-all duration-300 bg-white/50 text-gray-900 placeholder-gray-500 hover:bg-white/80 hover:border-orange-300"
              required
            />
          </div>
        </div>

        {/* Phone & Password Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Phone */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <Phone className="w-4 h-4 text-amber-600" />
              Phone Number
            </label>
            <div
              className="relative group"
              onMouseEnter={() => handleMouseEnter("phone")}
              onMouseLeave={() => handleMouseLeave("phone")}
            >
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Phone className="h-5 w-5 text-gray-500 group-hover:text-amber-600 transition-colors" />
              </div>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+91 98765 43210"
                className="w-full pl-12 pr-4 py-3 border border-amber-200/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400/30 focus:border-amber-300 transition-all duration-300 bg-white/50 text-gray-900 placeholder-gray-500 hover:bg-white/80 hover:border-amber-300"
                required
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <Lock className="w-4 h-4 text-yellow-600" />
              Password
            </label>
            <div
              className="relative group"
              onMouseEnter={() => handleMouseEnter("password")}
              onMouseLeave={() => handleMouseLeave("password")}
            >
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-500 group-hover:text-yellow-600 transition-colors" />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Create strong password"
                className="w-full pl-12 pr-12 py-3 border border-yellow-200/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400/30 focus:border-yellow-300 transition-all duration-300 bg-white/50 text-gray-900 placeholder-gray-500 hover:bg-white/80 hover:border-yellow-300"
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-4 flex items-center group"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-yellow-400 hover:text-yellow-500 transition-colors" />
                ) : (
                  <Eye className="h-5 w-5 text-yellow-400 hover:text-yellow-500 transition-colors" />
                )}
              </button>

              {/* Password strength indicator */}
              {formData.password && (
                <div className="absolute -bottom-5 left-0 right-0">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-amber-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${
                          passwordStrength === "weak"
                            ? "bg-gradient-to-r from-red-500 to-orange-400"
                            : passwordStrength === "medium"
                              ? "bg-gradient-to-r from-yellow-500 to-amber-400"
                              : "bg-gradient-to-r from-green-500 to-emerald-400"
                        }`}
                        style={{
                          width: `${(formData.password.length / 16) * 100}%`,
                        }}
                      />
                    </div>
                    <span className="text-xs font-medium text-amber-700">
                      {passwordStrength.charAt(0).toUpperCase() +
                        passwordStrength.slice(1)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Terms & Conditions */}
        <div className="flex items-start space-x-3 pt-2">
          <div className="relative flex-shrink-0 mt-0.5">
            <input
              type="checkbox"
              id="terms"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="sr-only"
            />
            <motion.div
              className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-all duration-200 ${
                agreedToTerms
                  ? "bg-gradient-to-r from-amber-500 to-orange-500 border-amber-500"
                  : "border-amber-300 hover:border-amber-500"
              }`}
              onClick={() => setAgreedToTerms(!agreedToTerms)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              {agreedToTerms && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <Check className="w-3 h-3 text-white" />
                </motion.div>
              )}
            </motion.div>
          </div>
          <label
            htmlFor="terms"
            className="text-sm text-gray-600 leading-relaxed"
          >
            I agree to the{" "}
            <Link
              to="/terms"
              className="text-amber-600 hover:text-orange-600 font-medium"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              to="/privacy"
              className="text-amber-600 hover:text-orange-600 font-medium"
            >
              Privacy Policy
            </Link>
          </label>
        </div>

        {/* Submit Button */}
        <motion.button
          type="submit"
          disabled={loading || !agreedToTerms}
          className="relative w-full bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-white py-3.5 rounded-xl hover:from-amber-600 hover:via-orange-600 hover:to-amber-600 transition-all duration-300 flex items-center justify-center font-semibold shadow-lg hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed group overflow-hidden"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {/* Animated background */}
          <motion.div
            animate={{
              x: ["-100%", "200%"],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "linear",
            }}
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12"
          />
          <div className="relative flex items-center justify-center gap-3">
            {loading ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                  <Loader2 className="w-5 h-5" />
                </motion.div>
                <span>Launching Account...</span>
              </>
            ) : (
              <>
                <ShieldCheck className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                <span>Start Journey</span>
              </>
            )}
          </div>
        </motion.button>
      </form>
    </div>
  );
};

export default Register;
